import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  CheckCircle2,
  AlertTriangle,
  ScrollText,
  Bot,
  Cloud,
  ChevronRight,
} from 'lucide-react';
import { api } from '@/api/client';
import { StatCard, type StatTone } from '@/components/ui/stat-card';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { cn, taxYearLabel } from '@/lib/utils';
import { humanizeAuditAction } from '@/lib/audit';
import type {
  AdminDashboardStats,
  TriageItem,
  AdminModuleAuditListResponse,
  AdminModuleAuditEntry,
} from '@/api/generated/types.gen';

export const Route = createFileRoute('/admin/')({
  component: AdminDashboard,
});

type Severity = 'maroon' | 'ochre' | 'forest';

const TRIAGE_CAP = 8;

function AdminDashboard() {
  const stats = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => api<AdminDashboardStats>('/admin/stats'),
  });
  const triage = useQuery({
    queryKey: ['admin', 'triage', TRIAGE_CAP],
    queryFn: () => api<TriageItem[]>(`/admin/triage?take=${TRIAGE_CAP}`),
  });
  const audit = useQuery({
    queryKey: ['admin', 'audit', 'recent'],
    queryFn: () => api<AdminModuleAuditListResponse>('/admin/audit?take=8'),
    retry: false,
  });

  const greeting = new Date().toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <div className="space-y-8">
      <AdminPageHeader
        eyebrow="Reviewer console"
        title={`Today, ${greeting}`}
        description="Returns waiting on you, the agent's open concerns, and what moved through the system since you last looked."
      />

      <TriageSection
        items={triage.data ?? []}
        isLoading={triage.isLoading}
        isError={triage.isError}
      />

      <PipelineStats data={stats.data} isLoading={stats.isLoading} />

      <AgentHealth data={stats.data} />

      <RecentActivity
        entries={audit.data?.items ?? []}
        isLoading={audit.isLoading}
        isError={audit.isError}
      />
    </div>
  );
}

function TriageSection({
  items,
  isLoading,
  isError,
}: {
  items: TriageItem[];
  isLoading: boolean;
  isError: boolean;
}) {
  const overflow = items.length >= TRIAGE_CAP;
  const severity = (raw: string | null | undefined): Severity =>
    raw === 'maroon' || raw === 'ochre' || raw === 'forest' ? raw : 'forest';
  return (
    <section>
      <div className="flex items-baseline justify-between">
        <h2 className="font-serif text-xl font-medium text-(--color-ink)">Needs your eyes</h2>
        {overflow && (
          <Link
            to="/admin/returns"
            search={{ filter: 'needs-attention' }}
            className="gov-link text-[13px] font-medium"
          >
            View all returns
          </Link>
        )}
      </div>
      <div className="mt-4 overflow-hidden rounded-(--radius) border border-(--color-rule) bg-(--color-card)">
        {isLoading ? (
          <EmptyRow>Loading queue…</EmptyRow>
        ) : isError ? (
          <EmptyRow>Could not load the triage queue.</EmptyRow>
        ) : items.length === 0 ? (
          <EmptyRow>Nothing in the queue. Quiet day.</EmptyRow>
        ) : (
          <ul className="divide-y divide-(--color-rule)">
            {items.map((item) => (
              <li key={`${item.returnId}-${item.reasonCode}`}>
                <Link
                  to="/admin/returns/$id"
                  params={{ id: String(item.returnId) }}
                  className="grid grid-cols-[8px_1fr_auto_auto] items-center gap-4 py-3.5 pr-5 hover:bg-(--color-paper-2)"
                >
                  <span
                    aria-hidden
                    className={cn('h-full w-[3px]', SEVERITY_BAR[severity(item.severity)])}
                  />
                  <div className="min-w-0">
                    <div className="truncate text-[14px] font-medium text-(--color-ink)">
                      {item.taxpayerName}
                      <span className="ml-2 text-[12px] font-normal text-(--color-ink-muted)">
                        FY {taxYearLabel(Number(item.year))}
                      </span>
                    </div>
                    <div className="mt-0.5 truncate text-[12px] text-(--color-ink-muted)">
                      {item.reasonText}
                    </div>
                  </div>
                  <div className="font-mono text-[11px] tabular-nums text-(--color-ink-muted)">
                    {formatRelativeShort(item.surfacedAt)}
                  </div>
                  <ChevronRight className="size-4 text-(--color-ink-muted)" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function PipelineStats({
  data,
  isLoading,
}: {
  data: AdminDashboardStats | undefined;
  isLoading: boolean;
}) {
  const lodgeFailed = Number(data?.lodgeFailed ?? 0);
  const cols = lodgeFailed > 0 ? 'md:grid-cols-5' : 'md:grid-cols-4';
  return (
    <section>
      <div className="section-ref">Pipeline</div>
      <div
        className={cn(
          'mt-3 grid gap-px overflow-hidden rounded-(--radius) bg-(--color-rule)',
          cols,
        )}
      >
        <StatCard label="Drafts" value={loadingVal(data?.drafts, isLoading)} icon={FileText} tone="navy" />
        <StatCard
          label="Lodged this FY"
          value={loadingVal(data?.lodgedThisFy, isLoading)}
          icon={CheckCircle2}
          tone="forest"
        />
        <StatCard label="Lodged (total)" value={loadingVal(data?.lodged, isLoading)} icon={CheckCircle2} tone="navy" />
        <StatCard
          label="Total returns"
          value={loadingVal(data?.totalReturns, isLoading)}
          icon={FileText}
          tone="navy"
        />
        {lodgeFailed > 0 && (
          <StatCard
            label="Lodge failed"
            value={lodgeFailed}
            icon={AlertTriangle}
            tone="maroon"
          />
        )}
      </div>
    </section>
  );
}

function AgentHealth({ data }: { data: AdminDashboardStats | undefined }) {
  return (
    <section>
      <div className="section-ref">Agent health</div>
      <div className="mt-3 grid gap-4 md:grid-cols-2">
        <HealthPanel
          icon={Bot}
          tone="ochre"
          title="Open concerns"
          metric={Number(data?.openAgentConcerns ?? 0)}
          caption="Assistant notes waiting on a resolution"
          to="/admin/agent/concerns"
        />
        <HealthPanel
          icon={Cloud}
          tone="navy"
          title="Prefill not OK"
          metric={Number(data?.prefillNotOk ?? 0)}
          caption="Returns whose latest ATO prefill snapshot failed or expired"
          to="/admin/agent/prefill"
        />
      </div>
    </section>
  );
}

function HealthPanel({
  icon: Icon,
  tone,
  title,
  metric,
  caption,
  to,
}: {
  icon: typeof Bot;
  tone: StatTone;
  title: string;
  metric: number;
  caption: string;
  to?: string;
}) {
  const accent =
    tone === 'navy'
      ? 'bg-(--color-navy-soft) text-(--color-navy)'
      : tone === 'ochre'
        ? 'bg-(--color-ochre-soft) text-(--color-ochre)'
        : tone === 'maroon'
          ? 'bg-(--color-maroon-soft) text-(--color-maroon)'
          : 'bg-(--color-forest-soft) text-(--color-forest)';
  const body = (
    <>
      <div className={cn('flex size-10 items-center justify-center rounded-(--radius-sm)', accent)}>
        <Icon className="size-5" strokeWidth={1.6} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-(--color-ink)">{title}</div>
        <div className="mt-1 text-[12px] leading-relaxed text-(--color-ink-muted)">{caption}</div>
      </div>
      <div className="font-mono text-2xl font-semibold tabular-nums text-(--color-ink)">
        {metric}
      </div>
    </>
  );
  const shell = 'flex items-start gap-4 rounded-(--radius) border border-(--color-rule) bg-(--color-card) px-5 py-4';
  if (to) {
    return (
      <Link to={to} className={cn(shell, 'transition-colors hover:bg-(--color-paper-2)')}>
        {body}
      </Link>
    );
  }
  return <div className={shell}>{body}</div>;
}

function RecentActivity({
  entries,
  isLoading,
  isError,
}: {
  entries: AdminModuleAuditEntry[];
  isLoading: boolean;
  isError: boolean;
}) {
  return (
    <section>
      <div className="flex items-baseline justify-between">
        <h2 className="font-serif text-xl font-medium text-(--color-ink)">Recent activity</h2>
        <Link to="/admin/audit" className="gov-link text-[13px] font-medium">
          View audit log
        </Link>
      </div>
      <div className="mt-4 overflow-hidden rounded-(--radius) border border-(--color-rule) bg-(--color-card)">
        {isLoading ? (
          <EmptyRow>Loading activity…</EmptyRow>
        ) : isError ? (
          <EmptyRow>Audit endpoint unavailable.</EmptyRow>
        ) : entries.length === 0 ? (
          <EmptyRow>No recent activity.</EmptyRow>
        ) : (
          <ul className="divide-y divide-(--color-rule)">
            {entries.slice(0, 8).map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-3 px-4 py-3 text-[13px]">
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'flex size-7 items-center justify-center rounded-(--radius-sm)',
                      a.source === 'Agent'
                        ? 'bg-(--color-navy-soft) text-(--color-navy)'
                        : 'bg-(--color-paper-3) text-(--color-ink-2)',
                    )}
                  >
                    {a.source === 'Agent' ? (
                      <Bot className="size-3.5" strokeWidth={1.7} />
                    ) : (
                      <ScrollText className="size-3.5" strokeWidth={1.7} />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate font-medium text-(--color-ink)">
                      {humanizeAuditAction(a.action)}
                    </div>
                    <div className="truncate text-[12px] text-(--color-ink-muted)">
                      {a.userName ?? (a.source === 'Agent' ? 'NightAI' : 'System')}
                      {a.returnId ? ` · return #${a.returnId}` : ''}
                    </div>
                  </div>
                </div>
                <div className="font-mono text-[11px] tabular-nums text-(--color-ink-muted)">
                  {formatRelativeShort(a.timestamp)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

const SEVERITY_BAR: Record<Severity, string> = {
  maroon: 'bg-(--color-maroon)',
  ochre: 'bg-(--color-ochre)',
  forest: 'bg-(--color-forest)',
};

function EmptyRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-5 py-10 text-center text-[13px] text-(--color-ink-muted)">{children}</div>
  );
}

function loadingVal(value: number | string | undefined, isLoading: boolean): string | number {
  if (value !== undefined) return Number(value);
  return isLoading ? '—' : 0;
}

function formatRelativeShort(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

